import { normalizeTaskType, STEP_TYPES } from '../config/workspaceTypes';

/* ────────────────────────────────────────────────────────────────────────
 * WORKED EXAMPLES ENGINE
 *
 * Generates the five SOLVE-tab explanations for a lesson:
 *   practical (WRITE IT) · logic (EXPLAIN IT) · analogy (COMPARE IT) ·
 *   story (USE IT) · visual (SEE IT)
 *
 * Public API is unchanged from the previous version:
 *   detectClauses(text)               -> SQL clause flags
 *   resolveLessonTaskType(lesson)      -> 'sql' | 'excel' | 'python' | 'data-analysis' | ...
 *   buildWorkedExamples(lesson, taskType) -> { practical, logic, analogy, story, visual }
 *   SQL_SAMPLE_SETUP                   -> seed SQL for the practice sandbox
 *
 * New, additive exports (safe to ignore if nothing imports them yet):
 *   detectPythonPatterns(text)
 *   detectExcelPatterns(text)
 *
 * Design notes for whoever touches this next:
 * - All keyword detection uses word-boundary regex, not substring search.
 *   Substring search on raw text has real false positives in this domain —
 *   "Laptop" contains "TOP ", "CHECKSUM " contains "SUM ", and prose like
 *   "deal with order data" contains " WITH " and would have falsely flagged
 *   a CTE. Regex with \b fixes all of these.
 * - Python and Excel lessons used to fall through to one generic,
 *   SQL-flavoured set of steps/analogies regardless of what the lesson was
 *   actually about. They now get their own pattern detection and branches,
 *   same as SQL always had.
 * - Domain inference (ecommerce / finance / healthcare / construction /
 *   generic) is computed once and reused across the story AND visual
 *   lenses, so the worked example reads as one consistent scenario instead
 *   of generic placeholder data bolted onto a domain-specific story.
 * ──────────────────────────────────────────────────────────────────────── */

// ── Small generic helpers ───────────────────────────────────────────────

/** Render a clean markdown table from headers + row arrays. */
function renderMarkdownTable(headers, rows) {
  const headerLine = `| ${headers.join(' | ')} |`;
  const dividerLine = `|${headers.map(() => '---').join('|')}|`;
  const rowLines = rows.map((row) => `| ${row.join(' | ')} |`);
  return [headerLine, dividerLine, ...rowLines].join('\n');
}

/** Number a list of plain-text steps as "1. ...", "2. ...". */
function numberSteps(steps) {
  return steps.map((step, i) => `${i + 1}. ${step}`).join('\n');
}

function runPatternMap(text, patternMap) {
  const result = {};
  for (const [key, pattern] of Object.entries(patternMap)) {
    result[key] = pattern.test(text);
  }
  return result;
}

function splitScenarioPhrases(text = '') {
  return text
    .replace(/\*\*/g, '')
    .replace(/\bSector:\s*([^.\n]+)(?=\s+Scenario:|\n|\.|$)/i, '')
    .replace(/\bScenario:\s*/i, '')
    .split(/(?<=[.!?])\s+|—|;|\n+/)
    .map((part) => part.trim())
    .filter((part) => part.length > 12)
    .slice(0, 6);
}

// ── Keyword / clause detection (word-boundary regex) ────────────────────

const SQL_CLAUSE_PATTERNS = {
  hasGroupBy: /\bGROUP\s+BY\b/i,
  hasHaving: /\bHAVING\b/i,
  hasWhere: /\bWHERE\b|\bFILTER\b/i,
  hasOrderBy: /\bORDER\s+BY\b|\bSORT\b/i,
  hasJoin: /\bJOIN\b/i,
  hasCount: /\bCOUNT\b/i,
  hasSum: /\bSUM\s*\(|\bSUM\b/i,
  hasAvg: /\bAVG\s*\(|\bAVERAGE\b/i,
  hasLimit: /\bLIMIT\b|\bTOP\b/i,
  hasCase: /\bCASE\s+WHEN\b|\bCASE\b\s*[\r\n]/i,
  // Requires "WITH <name> AS (" rather than the bare English word "with",
  // which appears constantly in lesson prose and is not a CTE signal.
  hasCte: /\bWITH\b\s+\w+\s+AS\s*\(/i,
  hasWindow: /\bOVER\s*\(/i,
  hasDistinct: /\bDISTINCT\b/i,
};

/** SQL clause flags for a block of text. Same shape as before, regex-based now. */
export function detectClauses(text = '') {
  return runPatternMap(text, SQL_CLAUSE_PATTERNS);
}

const PYTHON_PATTERNS = {
  hasGroupAgg: /\b(groupby|agg|aggregate)\b/i,
  hasMerge: /\b(merge|join)\b/i,
  hasApply: /\b(apply|lambda|map)\b/i,
  hasPlotting: /\b(plot|matplotlib|seaborn|chart|visuali[sz]e)\b/i,
  hasPivot: /\bpivot(_table)?\b/i,
  hasControlFlow: /\b(def|class|for|while)\b/i,
  hasFileIO: /\b(read_csv|read_excel|to_csv|json|api)\b/i,
};

/** New: pandas/Python pattern flags, mirroring detectClauses for SQL. */
export function detectPythonPatterns(text = '') {
  return runPatternMap(text, PYTHON_PATTERNS);
}

const EXCEL_PATTERNS = {
  hasLookup: /\b(vlookup|xlookup|hlookup)\b/i,
  hasConditionalAgg: /\b(sumifs?|countifs?|averageifs?)\b/i,
  hasPivotTable: /\bpivot\s*table\b/i,
  hasPowerQuery: /\bpower\s*query\b/i,
  hasFormatting: /\bconditional\s*formatting\b/i,
};

/** New: Excel pattern flags, mirroring detectClauses for SQL. */
export function detectExcelPatterns(text = '') {
  return runPatternMap(text, EXCEL_PATTERNS);
}

// ── Task type resolution ─────────────────────────────────────────────────

const TASK_TYPE_SIGNAL_PATTERNS = {
  sql: /\b(select|join|where|having|group by|order by|sql|duckdb|cte)\b/gi,
  excel: /\b(vlookup|xlookup|sumif|sumifs|countif|pivottable|power query)\b/gi,
  python: /\b(python|pandas|numpy|matplotlib|seaborn|plotly|dataframe|scipy|etl|pathlib|glob|logging|read_csv|to_csv|venv|pip)\b/gi,
};

const PYTHON_ETL_SIGNAL = /\b(python|etl|pathlib|glob|logging|read_csv|to_csv|pandas|dataframe|venv|pip)\b/i;

function scoreTaskTypeSignals(text) {
  const scores = {};
  for (const [type, pattern] of Object.entries(TASK_TYPE_SIGNAL_PATTERNS)) {
    scores[type] = (text.match(pattern) || []).length;
  }
  return scores;
}

/**
 * Tie-break rule (unchanged from the original): sql wins ties with excel,
 * but anything must strictly beat python to count it as the dominant
 * signal. A score of 1 is treated as noise, not a real signal — hence the
 * "> 1" thresholds. Python's dominant case maps to the 'data-analysis'
 * task type label, matching the rest of the platform's vocabulary.
 */
function pickDominantTaskType(scores) {
  const { sql, excel, python } = scores;
  if (sql > 1 && sql >= excel && sql > python) return 'sql';
  if (excel > 1 && excel > sql && excel > python) return 'excel';
  if (python > 1 && python > sql && python > excel) return 'data-analysis';
  return null;
}

export function resolveLessonTaskType(lesson = {}) {
  const phase = (lesson.phase || '').toLowerCase().trim();
  if (phase === 'sql') return 'sql';
  if (phase === 'excel') return 'excel';
  if (phase === 'python') {
    return lesson.learning_path_key === 'python' ? 'python' : 'data-analysis';
  }
  if (lesson.task_type) return normalizeTaskType(lesson.task_type);

  const text = [lesson.title, lesson.concept, lesson.lesson_prompt]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (PYTHON_ETL_SIGNAL.test(text)) return 'python';

  return pickDominantTaskType(scoreTaskTypeSignals(text)) || normalizeTaskType(lesson.learning_path_key);
}

export function buildLessonSolveOverrides(lesson = {}, taskType = 'generic', conceptTools = null) {
  const targetQuestion = lesson.lesson_prompt || lesson.deliverable || lesson.title || 'this task';
  const deliverable = lesson.deliverable || 'a clear, reviewable output';
  const scenario = (lesson.concept || '').split(/\n\n?---/)[0]?.replace(/\*\*/g, '').trim();
  const scenarioFocus = scenario || targetQuestion;
  const phraseList = splitScenarioPhrases(scenarioFocus);
  const phrasePrompt = phraseList.length
    ? `Break these scenario phrases into sub-questions:\n${phraseList.map((phrase, index) => `${index + 1}. ${phrase}`).join('\n')}`
    : `Break this scenario into distinct phrases: ${scenarioFocus}`;

  const splitPromptsByType = {
    python: [
      phrasePrompt,
      'Which phrase tells you the input, which phrase tells you the process, and which phrase tells you the expected output?',
      `Turn the scenario into 3-5 Python ETL sub-questions before coding. Keep the deliverable in view: ${deliverable}`,
    ],
    'data-analysis': [
      phrasePrompt,
      'Which phrase describes the stakeholder, the decision, the data, and the required evidence?',
      `Turn the scenario into 3-5 analysis sub-questions before calculating. Keep the deliverable in view: ${deliverable}`,
    ],
    sql: [
      phrasePrompt,
      'Which phrase tells you the required rows, columns, filters, groups, joins, or ordering?',
      `Turn the scenario into 3-5 query sub-questions before writing SQL. Keep the deliverable in view: ${deliverable}`,
    ],
    excel: [
      phrasePrompt,
      'Which phrase tells you the audience, the data, the visual decision, and the time-to-understand constraint?',
      `Turn the scenario into 3-5 workbook or chart sub-questions before building. Keep the deliverable in view: ${deliverable}`,
    ],
    generic: [
      phrasePrompt,
      'Which phrase describes the stakeholder, input, constraint, and expected output?',
      `Turn the scenario into 3-5 sub-questions before starting. Keep the deliverable in view: ${deliverable}`,
    ],
  };

  const observePromptsByType = {
    python: [
      'What are you given: files, folders, columns, expected row counts, or messy input conditions?',
      'What is expected from you: script, functions, logs, output file, or commit evidence?',
      'What risks must you watch before coding: bad delimiter, encoding, missing file, renamed column, or row-count mismatch?',
    ],
    'data-analysis': [
      'What are you given: dataset, columns, business context, stakeholder, or assumptions?',
      'What is expected from you: answer, chart, summary table, recommendation, or notebook?',
      'What risks could mislead the answer: missing values, duplicates, wrong grain, outliers, or unclear definitions?',
    ],
    sql: [
      'What are you given: tables, fields, keys, metric definitions, or filters?',
      'What is expected from you: result columns, grain, sort order, or business answer?',
      'What risks could make the query wrong: duplicate joins, wrong grain, missing filter, or overcounting?',
    ],
    excel: [
      'What are you given: workbook, rows, columns, formulas, targets, actuals, or visual constraints?',
      'What is expected from you: table, formula output, chart, dashboard sheet, or presentation-ready file?',
      'What risks could make the workbook misleading: wrong chart type, hidden raw issue, clutter, or unreadable labels?',
    ],
    generic: [
      'What are you given?',
      'What is expected from you?',
      'What could go wrong if you misunderstand the scenario?',
    ],
  };

  const verifyByType = {
    python: {
      language: 'python',
      placeholder: '# Repo proof: foundation/src/etl.py\n# Paste only the smallest runnable proof or key snippet here.\n# Example: verify row counts, output file, or required columns.\nassert rows_out <= rows_in\nassert rows_dropped >= 0',
      hint: 'Now solve the smallest reliable version and prove it works. For ETL, row counts and output paths are usually the first checks.',
    },
    'data-analysis': {
      language: 'python',
      placeholder: 'import pandas as pd\n\n# Solve the analysis question, then verify the data behind it\nassert df.shape[0] > 0\nassert df.columns.notna().all()',
      hint: 'Solve the analysis question, then verify the data behind the answer before trusting the insight.',
    },
    sql: {
      language: 'duckdb',
      placeholder: '-- Write the query, then verify the grain and row count\nSELECT COUNT(*) AS row_count\nFROM lesson_data;',
      hint: 'Solve with the query, then verify the grain, row count, and filters before presenting the result.',
    },
    excel: {
      prompts: [
        'What chart, formula, or workbook output will answer the target question?',
        'What manual spot-check proves the result is correct against the raw data?',
        'What would make the result fail the stakeholder need: wrong chart type, unclear label, bad calculation, or clutter?',
      ],
    },
  };

  const evolveByType = {
    python: {
      type: STEP_TYPES.STRUCTURED,
      prompts: [
        'What would you optimise after the first working version: structure, naming, logging, errors, or reuse?',
        'What evidence would you document so another learner or recruiter can review the work?',
        'What would you commit, screenshot, or explain in your journal?',
      ],
    },
    'data-analysis': {
      type: STEP_TYPES.STRUCTURED,
      prompts: [
        'What would you simplify or improve after the first answer: chart, wording, assumptions, or reproducibility?',
        'What evidence would you document so another analyst can trust the conclusion?',
        'What would you commit, screenshot, or explain in your journal?',
      ],
    },
    sql: {
      type: STEP_TYPES.STRUCTURED,
      prompts: [
        'What would you optimise after the first query: readability, CTEs, aliases, filters, or validation?',
        'What assumptions or checks should be documented with the query?',
        'What would you commit, screenshot, or explain in your journal?',
      ],
    },
    excel: {
      type: STEP_TYPES.STRUCTURED,
      prompts: [
        'What would you optimise after the first working workbook: layout, labels, colour, chart choice, or clutter?',
        'What documentation would help someone understand how to review the workbook?',
        'What would you save, screenshot, or explain in your journal?',
      ],
    },
  };

  return {
    split: {
      title: 'Split this scenario',
      instruction: 'Understand the problem first. Break the scenario into phrases and sub-questions before solving.',
      prompts: splitPromptsByType[taskType] || splitPromptsByType.generic,
      example: scenarioFocus,
    },
    observe: {
      type: STEP_TYPES.STRUCTURED,
      title: 'Observe what is given and expected',
      instruction: 'Identify the givens, expected output, constraints, and risks before choosing tools.',
      prompts: observePromptsByType[taskType] || observePromptsByType.generic,
    },
    look: {
      title: 'Choose tools for this lesson',
      instruction: 'Select the smallest set of tools that fits the scenario and target question.',
      ...(conceptTools ? { tools: conceptTools } : {}),
    },
    verify: {
      title: 'Solve and verify',
      instruction: 'Build the first correct answer, then prove it meets the scenario and target question.',
      ...(verifyByType[taskType] || {}),
    },
    evolve: {
      title: 'Optimise, document, and evolve',
      instruction: 'Improve the first answer, make it easier to review, and document the evidence.',
      ...(evolveByType[taskType] || {}),
    },
  };
}

// ── Domain inference (reused across the story and visual lenses) ───────

const DOMAIN_PROFILES = [
  {
    test: /e-?commerce|\border|shop|retail\b/i,
    label: 'an e-commerce company',
    categories: ['Electronics', 'Clothing', 'Home & Garden', 'Sports'],
    entityNoun: 'order',
    people: ['Alice Chen', 'Bob Smith', 'Carol White'],
  },
  {
    test: /\bbank|financ|invest\b/i,
    label: 'a financial services firm',
    categories: ['Mortgages', 'Credit Cards', 'Investments', 'Savings'],
    entityNoun: 'transaction',
    people: ['Daniel Osei', 'Priya Nair', 'James Carter'],
  },
  {
    test: /\bhospital|health|patient|clinic\b/i,
    label: 'a healthcare provider',
    categories: ['Cardiology', 'Orthopaedics', 'Paediatrics', 'Radiology'],
    entityNoun: 'appointment',
    people: ['Dr. Amara Eze', 'Dr. Liam Foster', 'Dr. Sofia Marin'],
  },
  {
    test: /\bconstruction\b|\bsite\b|\bproperty\b|\bbuild(ing)?\b/i,
    label: 'a construction & property firm',
    categories: ['Groundworks', 'Electrical', 'Plumbing', 'Finishing'],
    entityNoun: 'work order',
    people: ['Tunde Bakare', 'Grace Owolabi', 'Mark Fielding'],
  },
];

const DEFAULT_DOMAIN_PROFILE = {
  label: 'a mid-size company',
  categories: ['Category A', 'Category B', 'Category C', 'Category D'],
  entityNoun: 'record',
  people: ['Alex Morgan', 'Jamie Lee', 'Sam Carter'],
};

function inferDomainProfile(text) {
  return DOMAIN_PROFILES.find((profile) => profile.test.test(text)) || DEFAULT_DOMAIN_PROFILE;
}

const ROLE_BY_TASK_TYPE = {
  sql: 'Junior Data Analyst',
  python: 'Junior Python Developer',
  'data-analysis': 'Junior Data Analyst',
  excel: 'Junior Financial Analyst',
};

const TOOL_BY_TASK_TYPE = {
  sql: 'our SQL client',
  python: 'our Jupyter notebook',
  'data-analysis': 'our Jupyter notebook',
  excel: 'Excel',
};

function extractScenarioFromConcept(concept = '') {
  return (concept || '')
    .split(/\n\n?---/)[0]
    .replace(/\*\*/g, '')
    .replace(/\bSector:\s*/i, 'Sector: ')
    .replace(/\bScenario:\s*/i, '\nScenario: ')
    .trim();
}

function scenarioTextFromCtx(ctx) {
  const scenario = extractScenarioFromConcept(ctx.concept);
  return scenario || ctx.prompt || ctx.title || '';
}

function targetQuestionFromCtx(ctx) {
  return ctx.lesson?.lesson_prompt || ctx.lesson?.deliverable || ctx.prompt || ctx.title || 'the target question';
}

function hasScenarioSignal(text = '') {
  return /\bscenario:|manager|director|client|stakeholder|needs to|wants to|asked|review meeting|at a glance|intervention|target|actual|deliverable\b/i.test(text);
}

function hasVisualDecisionSignal(text = '') {
  return /\b(chart|visual|dashboard|at a glance|10 seconds|target|actual|intervention|presentation-ready|line performance|production line)\b/i.test(text);
}

function scenarioPhraseLines(ctx) {
  const phrases = splitScenarioPhrases(scenarioTextFromCtx(ctx));
  if (phrases.length === 0) return '';
  return phrases.map((phrase, index) => `${index + 1}. ${phrase}`).join('\n');
}

function buildScenarioLogicExplanation(ctx) {
  const scenario = scenarioTextFromCtx(ctx);
  if (!hasScenarioSignal(scenario)) return null;

  const targetQuestion = targetQuestionFromCtx(ctx);
  const phraseLines = scenarioPhraseLines(ctx);
  const isVisualDecision = hasVisualDecisionSignal(`${scenario} ${targetQuestion}`);

  const steps = isVisualDecision
    ? [
      'Split the scenario into stakeholder, data, decision, and time-to-understand phrases',
      'Identify the audience and decision: who must act, and what must be obvious in seconds?',
      'Identify the given data: entities, measures, target values, actual values, and any time period',
      'Create the smallest summary table needed to support the visual',
      'Choose the visual that makes the decision clearest, not the fanciest chart',
      'Verify the visual against the source values, then remove clutter and add labels',
    ]
    : [
      'Split the scenario into stakeholder, input, expected output, constraints, and evidence',
      'Identify what is given before choosing a tool',
      'State what the finished answer must prove',
      'Choose the smallest method that can produce that proof',
      'Build the answer, then verify it against the scenario',
      'Document what changed, what was checked, and what the learner would improve next',
    ];

  return (
    `**Platform pseudocode — scenario first, tool second:**\n\n` +
    (phraseLines ? `**Scenario phrases:**\n${phraseLines}\n\n` : '') +
    `${numberSteps(steps)}\n\n` +
    `**Target question:** ${targetQuestion}\n\n` +
    `*The goal is not to rush into a formula or chart. First understand what the scenario is asking the learner to prove.*`
  );
}

function buildScenarioAnalogyExplanation(ctx) {
  const scenario = scenarioTextFromCtx(ctx);
  const targetQuestion = targetQuestionFromCtx(ctx);
  if (!hasScenarioSignal(scenario)) return null;

  if (hasVisualDecisionSignal(`${scenario} ${targetQuestion}`)) {
    return (
      `**Platform analogy — a control-room status board:**\n\n` +
      `A plant director does not need every row from the production log during a review meeting. They need a board that makes the decision obvious: which lines are on target, which are close to risk, and which need intervention now.\n\n` +
      `That is what the chart must do. The workbook may contain formulas and tables underneath, but the visible output should behave like a status board: fast to scan, labelled clearly, and focused on the action.\n\n` +
      `**Where it breaks down:** a status board can hide detail. Keep the source table and checks nearby so the visual can be audited when someone asks, "Why is this line red?"`
    );
  }

  return (
    `**Platform analogy — turning a briefing note into a decision board:**\n\n` +
    `The scenario is the briefing note. It tells you who needs help, what data is available, what decision must be made, and what the finished work must prove.\n\n` +
    `The analysis is the decision board. It should remove noise and make the next action easier to see.\n\n` +
    `**Where it breaks down:** real projects can have missing context. When the scenario does not say enough, write the assumption instead of guessing silently.`
  );
}

function buildScenarioStoryExplanation(ctx) {
  const scenario = scenarioTextFromCtx(ctx);
  if (!hasScenarioSignal(scenario)) return null;

  const targetQuestion = targetQuestionFromCtx(ctx);
  const isVisualDecision = hasVisualDecisionSignal(`${scenario} ${targetQuestion}`);
  const role = isVisualDecision ? 'Operations analyst' : (ROLE_BY_TASK_TYPE[ctx.taskType] || 'Junior Data Analyst');
  const tool = TOOL_BY_TASK_TYPE[ctx.taskType] || 'the right analysis tool';

  const steps = isVisualDecision
    ? [
      'Read the scenario and underline the audience: the person who must make the decision',
      'List the given measures: daily target and actual output for each production line',
      'Calculate the gap or achievement percentage so every line can be compared fairly',
      'Build a chart that shows intervention lines immediately, with labels and restrained colour',
      'Spot-check two lines against the raw data before sharing the file',
    ]
    : [
      'Read the scenario and write the target question in one sentence',
      'List the inputs, constraints, and expected evidence',
      `Use ${tool} to build the smallest reliable answer`,
      'Verify the result against the scenario before presenting it',
      'Document the decision, checks, and next improvement',
    ];

  return (
    `**Platform job story — use the scenario as the job brief:**\n\n` +
    `**Your role:** ${role}\n\n` +
    `**The job brief:**\n"${scenario.replace(/\n+/g, ' ')}"\n\n` +
    `**The decision question:**\n"${targetQuestion}"\n\n` +
    `**How you handle it:**\n${numberSteps(steps)}\n\n` +
    `The finished work is not just a file. It is evidence that the learner understood the business request, chose the right output, and checked it before sharing.`
  );
}

function buildScenarioVisualExplanation(ctx) {
  const scenario = scenarioTextFromCtx(ctx);
  const targetQuestion = targetQuestionFromCtx(ctx);
  if (!hasScenarioSignal(scenario) || !hasVisualDecisionSignal(`${scenario} ${targetQuestion}`)) return null;

  return (
    `**Platform output — what the finished answer should look like:**\n\n` +
    `The visible output should be a compact chart or dashboard view, supported by a small summary table.\n\n` +
    renderMarkdownTable(
      ['line', 'target', 'actual', 'gap', 'status'],
      [
        ['Line A', '1,000', '1,080', '+80', 'On target'],
        ['Line B', '1,000', '940', '-60', 'Watch'],
        ['Line C', '1,000', '810', '-190', 'Intervene'],
      ],
    ) +
    `\n\n` +
    `**Best visual:** a clustered bar chart comparing Target vs Actual for each line, with a clear title and a colour cue for lines below target.\n\n` +
    `**What someone should understand in 10 seconds:** which production lines need intervention, and how far they are from target.\n\n` +
    `**Verification check:** pick two lines, compare the chart values back to the raw target and actual columns, and confirm the gap calculation.`
  );
}

// ── EXPLAIN IT: pseudocode per task type ────────────────────────────────

function buildSqlPseudoSteps(cl) {
  const steps = ['Start with the source table — every row, no filters yet'];
  if (cl.hasWhere) steps.push("Filter (WHERE): discard rows that don't match your condition — do this early");
  if (cl.hasJoin) steps.push('Join (JOIN): bring in columns from a second table, matching on a shared key');
  if (cl.hasGroupBy) steps.push('Group (GROUP BY): collapse all rows that share the same value into a single summary row');

  const aggs = [
    cl.hasSum && 'SUM — total all values in the group',
    cl.hasCount && 'COUNT — count rows in the group',
    cl.hasAvg && 'AVG — average value in the group',
  ].filter(Boolean);
  if (aggs.length) steps.push(`Aggregate: for each group calculate — ${aggs.join('; ')}`);

  if (cl.hasHaving) steps.push("Filter groups (HAVING): remove groups whose aggregate doesn't meet the threshold — runs after GROUP BY");
  if (cl.hasCase) steps.push('Label (CASE WHEN): add a derived column — if condition then valueA else valueB');
  if (cl.hasWindow) steps.push('Window function (OVER): rank or compare each row against its neighbours without collapsing rows');
  if (cl.hasCte) steps.push('CTE (WITH …): name intermediate results so the main query reads like a story');
  if (cl.hasOrderBy) steps.push('Sort (ORDER BY DESC): rank the result — highest or most-recent first');
  if (cl.hasLimit) steps.push('Limit (LIMIT N): return only the top N rows');
  steps.push('SELECT: choose which columns appear in the final output');

  return numberSteps(steps);
}

function buildPythonPseudoSteps(py) {
  if (py.hasGroupAgg) {
    return numberSteps([
      'Load the dataset into a DataFrame',
      'Filter rows to the relevant subset (boolean indexing)',
      'groupby() the category column',
      'agg() / sum() / mean() to compute one figure per group',
      'sort_values() to rank the result',
      'head(n) to keep only the top N groups',
    ]);
  }
  if (py.hasMerge) {
    return numberSteps([
      'Load both DataFrames',
      'Confirm the shared key column exists in both',
      'merge() on that key (default is an inner join)',
      'Check the row count before/after — a merge can silently drop or duplicate rows',
      'Select only the columns you need for the final output',
    ]);
  }
  if (py.hasPlotting) {
    return numberSteps([
      'Load and shape the data into the structure the chart needs',
      'Choose the chart type that matches the question (trend → line, comparison → bar, distribution → histogram)',
      'Plot the figure (matplotlib/seaborn)',
      'Label the axes and title — an unlabelled chart is not finished',
      'Save or display the figure',
    ]);
  }
  if (py.hasPivot) {
    return numberSteps([
      'Load the dataset',
      'Choose the index (rows), columns, and values for the pivot',
      'pivot_table() with an aggregation function (sum/mean/count)',
      "Fill or drop missing combinations explicitly — don't leave NaN unexplained",
      'Read the table left-to-right per row, top-to-bottom per column',
    ]);
  }
  if (py.hasControlFlow) {
    return numberSteps([
      'Define the function signature — what goes in, what comes out',
      'Handle the edge cases first (empty input, missing values)',
      'Write the loop or conditional that does the actual work',
      "Return the result — don't just print it",
      'Test it against at least one known input/output pair',
    ]);
  }
  // Fallback only — used when no specific pandas/Python pattern is detected.
  return numberSteps([
    'Load the dataset',
    'Filter rows to the relevant subset',
    'Group by the category column',
    'Apply the aggregate (sum / count / mean) per group',
    'Sort from highest to lowest',
    'Return only the top N rows',
  ]);
}

function buildExcelPseudoSteps(xl) {
  if (xl.hasLookup) {
    return numberSteps([
      "Identify the lookup value (what you're searching for)",
      'Identify the lookup range (where to search) and the return range (what to bring back)',
      'VLOOKUP/XLOOKUP: match the lookup value against the first/key column',
      'Set exact-match mode explicitly — approximate match is the silent source of most lookup errors',
      'Fill down the formula and spot-check a few rows against the source data',
    ]);
  }
  if (xl.hasConditionalAgg) {
    return numberSteps([
      'Identify the range to evaluate the condition against',
      'Identify the condition itself (equals, greater than 40000, matches text)',
      'Keep the condition fixed — do not compare one full range against another full range inside COUNTIFS/SUMIFS',
      'Identify the range to sum/count/average once the condition is met',
      'SUMIF/COUNTIF/AVERAGEIF: combine the criteria range, fixed criterion, and result range into one formula',
      'For row-by-row comparisons like Sales > Target, add a helper column or use SUMPRODUCT',
      "Cross-check the total against a manual filter to confirm it's right",
    ]);
  }
  if (xl.hasPivotTable) {
    return numberSteps([
      'Confirm your data is a proper table — one header row, no merged cells, no blank rows',
      'Insert a PivotTable from that range',
      'Drag the category into Rows, the metric into Values',
      'Set the Values field to Sum/Count/Average — Excel defaults to Count for text columns',
      'Sort or filter the pivot to surface what matters',
    ]);
  }
  return numberSteps([
    'Confirm the data is clean — consistent types, no stray blanks',
    'Decide which formula matches the question (lookup, conditional sum, or pivot)',
    'Build the formula on one row first',
    'Fill it down and validate against a manual spot-check',
    'Format the result so it reads clearly',
  ]);
}

function buildLogicExplanation(ctx) {
  const scenarioExplanation = buildScenarioLogicExplanation(ctx);
  if (scenarioExplanation) return scenarioExplanation;

  const { taskType, clauses, py, xl } = ctx;
  const steps =
    taskType === 'sql' ? buildSqlPseudoSteps(clauses) : taskType === 'excel' ? buildExcelPseudoSteps(xl) : buildPythonPseudoSteps(py);

  return (
    `**Platform pseudocode — how we break this scenario into steps:**\n\n${steps}\n\n` +
    `*Each numbered step maps to one part of the solution. If you can write these steps, you already understand the solution.*`
  );
}

// ── WRITE IT ─────────────────────────────────────────────────────────────

function buildPracticalExplanation(ctx) {
  const { lesson, title, concept, taskType } = ctx;

  if (lesson.solve_model_answer?.trim()) {
    return `**Platform solution — read each comment, then write your own:**\n\n${lesson.solve_model_answer}`;
  }

  const codeMatch = concept.match(/```(sql|python|duckdb|excel)\n([\s\S]+?)```/i);
  if (codeMatch) {
    const lang = codeMatch[1].toLowerCase();
    return (
      `**Platform solution — study this before writing your own:**\n\n` +
      `\`\`\`${lang}\n${codeMatch[2].trim()}\n\`\`\`\n\n` +
      `*Read each line before scrolling to "Your turn".*`
    );
  }

  if (taskType === 'excel') {
    const formulaMatch = concept.match(/(=[A-Z]+\([^\n]*\))/i);
    if (formulaMatch) {
      return (
        `**Platform solution — study this formula before writing your own:**\n\n` +
        `\`${formulaMatch[1]}\`\n\n` +
        `*Read what each argument does before scrolling to "Your turn".*`
      );
    }
  }

  return (
    `**Platform solution**\n\n` +
    `A worked solution for **${title}** isn't published yet for this lesson.\n\n` +
    `Use the Concept Guide above as your reference, then attempt the exercise in "Your turn" below — you can come back and compare once a model answer is added.`
  );
}

// ── COMPARE IT ───────────────────────────────────────────────────────────

function buildSqlAnalogy(cl) {
  if (cl.hasGroupBy && cl.hasHaving) {
    return (
      `**Platform analogy — sorting expense receipts:**\n\n` +
      `You return from a business trip with a pile of receipts. You sort them into labelled envelopes — one envelope per expense category (meals, transport, hotels). That is **GROUP BY**: collapse rows that share the same value.\n\n` +
      `Once sorted, you total each envelope. That is **SUM()**: compute an aggregate per group.\n\n` +
      `You then throw away any envelope whose total is below the £50 minimum claimable. That is **HAVING**: filter groups by their aggregate — not individual receipts, but whole envelopes.\n\n` +
      `**WHERE vs HAVING:** WHERE decides which receipts go into the pile at all. HAVING decides which complete envelopes survive.\n\n` +
      `**Where it breaks down:** You can only physically sort a few hundred receipts. SQL handles millions in milliseconds.`
    );
  }
  if (cl.hasJoin) {
    return (
      `**Platform analogy — cross-referencing two spreadsheets:**\n\n` +
      `You have a sales spreadsheet (order_id, amount) and a customer spreadsheet (customer_id, name, city). To find which customer placed which order, you match rows by customer_id. That is exactly a **JOIN**: combine two tables on a shared key.\n\n` +
      `**INNER JOIN** keeps only rows that match in both spreadsheets.\n` +
      `**LEFT JOIN** keeps every row from the first spreadsheet, even if there is no match in the second.\n\n` +
      `**Where it breaks down:** In a spreadsheet you would use VLOOKUP — slow, error-prone, breaks on duplicates. SQL handles this correctly for millions of rows.`
    );
  }
  if (cl.hasWhere && cl.hasOrderBy) {
    return (
      `**Platform analogy — filtering and ranking job applications:**\n\n` +
      `A recruiter has 500 CVs. First they remove anyone who doesn't meet the minimum criteria — that is **WHERE**: filter rows before any other work happens.\n\n` +
      `The remaining CVs get stacked in order of experience, best first — that is **ORDER BY DESC**: sort what remains.\n\n` +
      `They pull the top 10 to interview — that is **LIMIT**: return only the first N rows.`
    );
  }
  return (
    `**Platform analogy — searching a well-organised filing cabinet:**\n\n` +
    `**SELECT** is the label on the folder you want — it decides what information comes back.\n` +
    `**FROM** is which filing cabinet (table) to open.\n` +
    `**WHERE** is your filter criteria — open only the drawers that match the condition.`
  );
}

function buildPythonAnalogy(py) {
  if (py.hasGroupAgg) {
    return (
      `**Platform analogy — sorting expense receipts:**\n\n` +
      `You sort a pile of receipts into labelled envelopes, one per category. That's **groupby()**: collapse rows that share the same value into a group.\n\n` +
      `You total each envelope. That's **.sum() / .agg()**: compute one figure per group.\n\n` +
      `**Where it breaks down:** sorting by hand works for a shoebox of receipts. pandas does the same thing across millions of rows in milliseconds.`
    );
  }
  if (py.hasMerge) {
    return (
      `**Platform analogy — cross-referencing two spreadsheets:**\n\n` +
      `You have two spreadsheets that share a key column. Matching rows between them by that key is exactly **.merge()**.\n\n` +
      `An inner merge keeps only rows that match in both. A left merge keeps every row from the first, even with no match in the second.\n\n` +
      `**Where it breaks down:** doing this by hand with VLOOKUP is slow and breaks on duplicate keys. merge() handles duplicates and scale correctly.`
    );
  }
  if (py.hasPlotting) {
    return (
      `**Platform analogy — a translator, not a table:**\n\n` +
      `A table answers "what are the numbers." A chart answers "what's the shape of the numbers" — at a glance, before anyone reads a single value.\n\n` +
      `Choosing the chart type is choosing the question: a line answers "is it going up or down," a bar answers "which is bigger," a histogram answers "how spread out is it."\n\n` +
      `**Where it breaks down:** a chart can mislead if the axis or scale is chosen badly — it's a translator, not a neutral fact.`
    );
  }
  return (
    `**Platform analogy — a spreadsheet you can program:**\n\n` +
    `A DataFrame is a spreadsheet where every action — filtering, sorting, combining — is a line of code instead of a mouse click. That's the entire advantage: the same five lines run correctly whether the sheet has 50 rows or 50 million.`
  );
}

function buildExcelAnalogy(xl) {
  if (xl.hasLookup) {
    return (
      `**Platform analogy — a librarian with an index card:**\n\n` +
      `You hand a librarian a book title (the lookup value). They check the index card catalogue (the lookup range) and tell you the shelf number (the return range). That's **VLOOKUP/XLOOKUP**: find a value, return what's next to it.\n\n` +
      `**Where it breaks down:** an approximate-match lookup is a librarian who hands you "close enough" — usually not what you want for exact codes, IDs, or names.`
    );
  }
  if (xl.hasConditionalAgg) {
    return (
      `**Platform analogy — a bouncer with a clipboard:**\n\n` +
      `A bouncer only lets in people who meet a fixed rule, then the clipboard tallies them. That's exactly **SUMIF/COUNTIF**: check a condition like Product = "SaaS" or Sales > 40000, then total only what passes.\n\n` +
      `**Where it breaks down:** COUNTIFS/SUMIFS can check several fixed rules at once, but they are not a row-by-row comparison engine. For "Sales is greater than that same row's Target," use a helper column or SUMPRODUCT.`
    );
  }
  if (xl.hasPivotTable) {
    return (
      `**Platform analogy — an automatic sorting machine:**\n\n` +
      `Feed in a messy pile of transactions; the machine sorts them into bins by category and totals each bin. That's a **PivotTable** — Rows decide the bins, Values decide what gets totalled.\n\n` +
      `**Where it breaks down:** the machine only sorts what you feed it cleanly. Merged cells and blank rows jam it.`
    );
  }
  return (
    `**Platform analogy — a calculator that remembers its formula:**\n\n` +
    `A regular calculator forgets the moment you press "=". A spreadsheet formula keeps the recipe — change one ingredient (an input cell) and every dependent result updates itself.`
  );
}

function buildAnalogyExplanation(ctx) {
  const scenarioExplanation = buildScenarioAnalogyExplanation(ctx);
  if (scenarioExplanation) return scenarioExplanation;

  const { taskType, clauses, py, xl } = ctx;
  if (taskType === 'sql') return buildSqlAnalogy(clauses);
  if (taskType === 'excel') return buildExcelAnalogy(xl);
  return buildPythonAnalogy(py);
}

// ── USE IT ───────────────────────────────────────────────────────────────

function buildSqlStorySentences(cl) {
  const sentences = [];
  if (cl.hasWhere) sentences.push('You decide on your filter first. Adding WHERE early cuts the dataset down before any grouping happens.');
  if (cl.hasGroupBy) sentences.push('You use GROUP BY to collapse individual rows into one summary row per category. Picture each group as a labelled envelope.');
  if (cl.hasSum || cl.hasCount) {
    const fns = [cl.hasSum && 'SUM()', cl.hasCount && 'COUNT()', cl.hasAvg && 'AVG()'].filter(Boolean).join(' and ');
    sentences.push(`You add ${fns} to compute the figures your manager needs — one number per group.`);
  }
  if (cl.hasHaving) sentences.push("HAVING filters out groups that don't meet the threshold. It runs after aggregation, so it sees group totals — not individual rows.");
  if (cl.hasOrderBy) sentences.push('ORDER BY DESC puts the highest-value results first — the format the business always expects.');
  return sentences;
}

function buildPythonStorySentences(py) {
  const sentences = [];
  if (py.hasGroupAgg) sentences.push('You groupby() the category column, then agg() to get one figure per group — the same logic as sorting receipts into envelopes.');
  if (py.hasMerge) sentences.push("You merge() the two tables on their shared key, then check the row count before and after — a quiet way to catch a join that silently dropped rows.");
  if (py.hasPlotting) sentences.push('You plot the result rather than dumping a table — a chart answers "what changed" faster than a column of numbers.');
  if (py.hasPivot) sentences.push('You build a pivot_table() with the category as the index and the metric as the value, so the summary reads in one glance.');
  if (sentences.length === 0) {
    sentences.push('You load the data into a DataFrame, work through it step by step, and check the shape of the result after each transformation.');
  }
  return sentences;
}

function buildExcelStorySentences(xl) {
  const sentences = [];
  if (xl.hasLookup) sentences.push('You use XLOOKUP to pull the matching value across, with exact-match mode switched on so there are no silent near-misses.');
  if (xl.hasConditionalAgg) sentences.push('You build a SUMIFS that totals only the rows meeting every condition at once, instead of filtering manually first.');
  if (xl.hasPivotTable) sentences.push('You drop the category into Rows and the metric into Values, and the PivotTable does the grouping for you.');
  if (sentences.length === 0) {
    sentences.push('You build the formula on one row first, fill it down, then spot-check a few results against the source data.');
  }
  return sentences;
}

function buildStoryExplanation(ctx) {
  const scenarioExplanation = buildScenarioStoryExplanation(ctx);
  if (scenarioExplanation) return scenarioExplanation;

  const { taskType, clauses, py, xl, domain, prompt, title, lesson } = ctx;
  const role = ROLE_BY_TASK_TYPE[taskType] || 'Junior Data Analyst';
  const tool = TOOL_BY_TASK_TYPE[taskType] || 'our analytics tool';

  const sentences =
    taskType === 'sql' ? buildSqlStorySentences(clauses) : taskType === 'excel' ? buildExcelStorySentences(xl) : buildPythonStorySentences(py);

  const problemType = clauses.hasGroupBy || py.hasGroupAgg || xl.hasPivotTable ? 'grouping and aggregation'
    : clauses.hasJoin || py.hasMerge || xl.hasLookup ? 'multi-table lookup'
      : 'filter and sort';

  const managerQuestion = lesson.deliverable || prompt || `Can you pull the ${title.toLowerCase()} report? We need it for the standup.`;

  const body = sentences.map((s) => `${s}\n`).join('\n');

  return (
    `**Platform job story — see how this plays out on the job:**\n\n` +
    `**Your role:** ${role} at ${domain.label}\n\n` +
    `**The question your manager asks:**\n"${managerQuestion}"\n\n` +
    `**How you answer it — step by step:**\n\n` +
    `You open ${tool}. You recognise it immediately as a ${problemType} problem — you have seen the pattern before.\n\n` +
    `${body}\n` +
    `A few minutes later the result is ready. You paste it into the standup notes. The manager says: "Perfect, exactly what I needed."`
  );
}

// ── SEE IT ───────────────────────────────────────────────────────────────

function buildSqlVisual(ctx) {
  const { clauses, domain, concept } = ctx;
  const codeForVisual = concept.match(/```(sql|duckdb)\n([\s\S]+?)```/i);
  let out = `**Platform output — what the result looks like:**\n\n`;

  if (codeForVisual) {
    out += `Run this query:\n\n\`\`\`${codeForVisual[1].toLowerCase()}\n${codeForVisual[2].trim()}\n\`\`\`\n\n`;
  }

  out += `**Expected output (example rows — your data will differ):**\n\n`;

  if (clauses.hasGroupBy && (clauses.hasSum || clauses.hasCount)) {
    const [c1, c2, c3, c4] = domain.categories;
    const headers = ['category', clauses.hasSum ? 'total_value' : 'count', clauses.hasSum && clauses.hasCount ? 'order_count' : null].filter(Boolean);
    const rowValues = clauses.hasSum
      ? [
        [c1, '24,580.00'],
        [c2, '18,200.00'],
        [c3, '9,450.00'],
        [c4, '7,800.00'],
      ]
      : [
        [c1, '82'],
        [c2, '145'],
        [c3, '63'],
        [c4, '41'],
      ];
    const rows = clauses.hasSum && clauses.hasCount
      ? rowValues.map((r, i) => [...r, [82, 145, 63, 41][i]])
      : rowValues;

    out += renderMarkdownTable(headers, rows);
    out += `\n\n*Rows ordered highest-first. Groups below the threshold are excluded (HAVING).*\n\n`;
    out += `**Reading the result:**\n`;
    out += `- First column: the grouped category — one row per unique value\n`;
    if (clauses.hasSum) out += `- \`total_value\` — the sum of all matching rows in that group\n`;
    if (clauses.hasCount) out += `- \`order_count\` — how many individual rows were in that group\n`;
  } else if (clauses.hasJoin) {
    const [p1, p2, p3] = domain.people;
    out += renderMarkdownTable(
      ['customer_name', `${domain.entityNoun}_id`, `${domain.entityNoun}_value`, `${domain.entityNoun}_date`],
      [
        [p1, '1001', '245.00', '2024-03-01'],
        [p2, '1002', '89.50', '2024-03-01'],
        [p3, '1003', '512.00', '2024-03-02'],
      ],
    );
    out += `\n\n*Each row is one ${domain.entityNoun} matched to its customer. INNER JOIN excludes customers with no ${domain.entityNoun}s.*`;
  } else {
    out += renderMarkdownTable(
      ['id', 'name', 'value', 'date'],
      [
        ['1', 'Row A', '150.00', '2024-03-01'],
        ['2', 'Row B', '89.50', '2024-03-02'],
        ['3', 'Row C', '320.00', '2024-03-03'],
      ],
    );
    out += `\n\n*Example rows — run the query in the practice section to see real output.*`;
  }

  out += `\n\nScroll down to write your own query and produce your own output.`;
  return out;
}

function buildPythonVisual(ctx) {
  const { py, domain } = ctx;
  let out = `**Platform output — what the result looks like:**\n\n`;

  if (py.hasPlotting) {
    out +=
      `Rather than a table, the output here is a chart — a single figure with labelled axes that makes the trend or comparison visible at a glance.\n\n` +
      `*Expect: a clear title, labelled x/y axes, and (for comparisons) a legend if there's more than one series.*\n\n` +
      `Scroll down to generate your own chart from the dataset and compare it against this description.`;
    return out;
  }

  out += `**Expected output (example rows — your data will differ):**\n\n`;
  if (py.hasGroupAgg) {
    const [c1, c2, c3, c4] = domain.categories;
    out += renderMarkdownTable(
      ['category', 'total_value'],
      [
        [c1, '24580.00'],
        [c2, '18200.00'],
        [c3, '9450.00'],
        [c4, '7800.00'],
      ],
    );
    out += `\n\n*A pandas \`groupby().sum()\` returns one row per category, indexed by the grouped column.*`;
  } else if (py.hasMerge) {
    const [p1, p2, p3] = domain.people;
    out += renderMarkdownTable(
      ['customer_name', `${domain.entityNoun}_id`, `${domain.entityNoun}_value`],
      [
        [p1, '1001', '245.00'],
        [p2, '1002', '89.50'],
        [p3, '1003', '512.00'],
      ],
    );
    out += `\n\n*Result of \`.merge()\` on the shared key — one row per matched pair.*`;
  } else {
    out += renderMarkdownTable(
      ['id', 'name', 'value'],
      [
        ['1', 'Row A', '150.00'],
        ['2', 'Row B', '89.50'],
        ['3', 'Row C', '320.00'],
      ],
    );
    out += `\n\n*Example rows — run the notebook cell in the practice section to see your real output.*`;
  }

  out += `\n\nScroll down to write your own code and produce your own output.`;
  return out;
}

function buildExcelVisual(ctx) {
  const { xl, domain } = ctx;
  let out = `**Platform output — what the result looks like:**\n\n`;

  if (xl.hasPivotTable) {
    const [c1, c2, c3, c4] = domain.categories;
    out += renderMarkdownTable(
      ['Row Labels', 'Sum of Value'],
      [
        [c1, '24,580.00'],
        [c2, '18,200.00'],
        [c3, '9,450.00'],
        [c4, '7,800.00'],
      ],
    );
    out += `\n\n*This is what the PivotTable field list produces once Rows and Values are set.*`;
  } else if (xl.hasLookup || xl.hasConditionalAgg) {
    out += renderMarkdownTable(
      ['Input', 'Formula result'],
      [
        ['A1042', '£245.00'],
        ['A1043', '£89.50'],
        ['A1044', '#N/A — no match found'],
      ],
    );
    out += `\n\n*The third row is included deliberately: a lookup with no match returns an error, not a blank — your formula should expect that.*`;
  } else {
    out += renderMarkdownTable(
      ['Column A', 'Column B', 'Formula result'],
      [
        ['Row A', '150.00', '=A2*1.1'],
        ['Row B', '89.50', '=A3*1.1'],
      ],
    );
    out += `\n\n*Build the formula on the first row, then fill down.*`;
  }

  out += `\n\nScroll down to build your own formula and produce your own output.`;
  return out;
}

function buildVisualExplanation(ctx) {
  const scenarioExplanation = buildScenarioVisualExplanation(ctx);
  if (scenarioExplanation) return scenarioExplanation;

  if (ctx.taskType === 'sql') return buildSqlVisual(ctx);
  if (ctx.taskType === 'excel') return buildExcelVisual(ctx);
  return buildPythonVisual(ctx);
}

// ── Orchestrator ─────────────────────────────────────────────────────────

export function buildWorkedExamples(lesson = {}, taskType) {
  const concept = lesson.concept || '';
  const prompt = lesson.lesson_prompt || lesson.deliverable || '';
  const title = lesson.title || '';
  const combined = `${title} ${concept} ${prompt}`;
  const resolvedTaskType = taskType || resolveLessonTaskType(lesson);

  const ctx = {
    lesson,
    taskType: resolvedTaskType,
    title,
    concept,
    prompt,
    combined,
    clauses: detectClauses(combined),
    py: detectPythonPatterns(combined),
    xl: detectExcelPatterns(combined),
    domain: inferDomainProfile(combined),
  };

  return {
    practical: { explanation: buildPracticalExplanation(ctx) },
    logic: { explanation: buildLogicExplanation(ctx) },
    analogy: { explanation: buildAnalogyExplanation(ctx) },
    story: { explanation: buildStoryExplanation(ctx) },
    visual: { explanation: buildVisualExplanation(ctx) },
  };
}

// ── Real-world analyst practice scenarios ────────────────────────────────

const SQL_SCENARIOS_BY_PATTERN = {
  groupHaving: [
    {
      prompt:
        "Your head of sales asks: \"Which product categories hit more than £200 in total order value? Show the category name and total — highest first.\"\n\nWrite a SQL query against the `orders` table using `GROUP BY product_category`, `SUM(order_value)`, `HAVING`, and `ORDER BY DESC`.",
      hint:
        "```duckdb\nSELECT product_category,\n       SUM(order_value) AS total_revenue\nFROM orders\nGROUP BY product_category\nHAVING SUM(order_value) > 200\nORDER BY total_revenue DESC;\n```",
    },
    {
      prompt:
        "Operations need a report: \"Which customers placed more than one order? List their name and order count — skip anyone who only ordered once.\"\n\nWrite a SQL query using `GROUP BY customer_name`, `COUNT(*) AS order_count`, and `HAVING COUNT(*) > 1`.",
      hint:
        "```duckdb\nSELECT customer_name,\n       COUNT(*) AS order_count\nFROM orders\nGROUP BY customer_name\nHAVING COUNT(*) > 1\nORDER BY order_count DESC;\n```",
    },
  ],
  whereOrder: [
    {
      prompt:
        "Your manager asks: \"Pull all completed orders worth more than £100, sorted by date — newest first.\"\n\nWrite a SQL query against `orders` using `WHERE status = 'completed' AND order_value > 100` and `ORDER BY order_date DESC`.",
      hint:
        "```duckdb\nSELECT order_id, customer_name, order_value, order_date\nFROM orders\nWHERE status = 'completed'\n  AND order_value > 100\nORDER BY order_date DESC;\n```",
    },
    {
      prompt:
        "Finance wants the top 5 most expensive Electronics orders: \"Give me the order_id, customer_name, and order_value — highest first.\"\n\nUse `WHERE product_category = 'Electronics'`, `ORDER BY order_value DESC`, and `LIMIT 5`.",
      hint:
        "```duckdb\nSELECT order_id, customer_name, order_value\nFROM orders\nWHERE product_category = 'Electronics'\nORDER BY order_value DESC\nLIMIT 5;\n```",
    },
  ],
  join: [
    {
      prompt:
        "The CRM team asks: \"Can you add the customer's city to our orders report? We need customer_name, city, and order_value for every order.\"\n\nWrite a SQL query that JOINs `orders` to `customers` on `customer_id`.",
      hint:
        "```duckdb\nSELECT o.customer_name, c.city, o.order_value\nFROM orders o\nJOIN customers c ON o.customer_id = c.customer_id\nORDER BY o.order_value DESC;\n```",
    },
    {
      prompt:
        "Marketing needs: \"Show each customer's total spend and their city — ordered by spend, highest first. Customers with no orders should still appear.\"\n\nWrite a SQL query using a LEFT JOIN, GROUP BY customer name and city, SUM(order_value).",
      hint:
        "```duckdb\nSELECT c.customer_name, c.city,\n       SUM(o.order_value) AS total_spend\nFROM customers c\nLEFT JOIN orders o ON c.customer_id = o.customer_id\nGROUP BY c.customer_name, c.city\nORDER BY total_spend DESC;\n```",
    },
  ],
  countGroup: [
    {
      prompt:
        "The ops lead needs the daily standup number: \"How many orders do we have per status (completed vs pending)?\"\n\nWrite a SQL query using `GROUP BY status`, `COUNT(*) AS order_count`, sorted by count descending.",
      hint:
        "```duckdb\nSELECT status, COUNT(*) AS order_count\nFROM orders\nGROUP BY status\nORDER BY order_count DESC;\n```",
    },
    {
      prompt:
        "Your manager wants a regional breakdown: \"How many orders came from each region? Show region and order count, highest first.\"\n\nWrite a query with `GROUP BY region`, `COUNT(*)`, ordered by count descending.",
      hint:
        "```duckdb\nSELECT region, COUNT(*) AS order_count\nFROM orders\nGROUP BY region\nORDER BY order_count DESC;\n```",
    },
  ],
  generic: [
    {
      prompt:
        "Your manager asks for a quick view: \"What are the five most expensive orders we've taken? Show me the order_id, customer_name, product_category, and order_value.\"\n\nWrite a SQL query using `ORDER BY order_value DESC` and `LIMIT 5`.",
      hint:
        "```duckdb\nSELECT order_id, customer_name,\n       product_category, order_value\nFROM orders\nORDER BY order_value DESC\nLIMIT 5;\n```",
    },
    {
      prompt:
        "Finance needs: \"Pull all completed orders from the North region. Show the customer name, product, and value.\"\n\nWrite a SQL query using `WHERE status = 'completed' AND region = 'North'`.",
      hint:
        "```duckdb\nSELECT customer_name, product_name, order_value\nFROM orders\nWHERE status = 'completed'\n  AND region = 'North'\nORDER BY order_value DESC;\n```",
    },
  ],
};

const PYTHON_SCENARIOS = [
  {
    prompt:
      "The sales team needs a summary from last month's export: \"What's our total revenue by product category? Show the top categories first.\"\n\nLoad the orders CSV, use `groupby('product_category')`, `sum()` on `order_value`, and `sort_values()` descending.",
    hint:
      "```python\nimport pandas as pd\ndf = pd.read_csv('orders.csv')\nsummary = (\n    df.groupby('product_category')['order_value']\n    .sum()\n    .reset_index()\n    .rename(columns={'order_value': 'total_revenue'})\n    .sort_values('total_revenue', ascending=False)\n)\nprint(summary)\n```",
  },
  {
    prompt:
      "Your manager asks: \"What's the average order value per region? I need to see if some regions are under-performing.\"\n\nUse `groupby('region')`, `mean()` on `order_value`, round to 2 decimal places.",
    hint:
      "```python\navg_by_region = (\n    df.groupby('region')['order_value']\n    .mean()\n    .round(2)\n    .reset_index()\n    .rename(columns={'order_value': 'avg_order_value'})\n    .sort_values('avg_order_value', ascending=False)\n)\nprint(avg_by_region)\n```",
  },
];

const EXCEL_SCENARIOS = [
  {
    prompt:
      "Finance has an Orders spreadsheet and wants to know: \"What's the total sales value for the Electronics category only?\"\n\nWrite a SUMIF formula that sums `order_value` where `product_category` equals \"Electronics\".",
    hint: "`=SUMIF(C:C, \"Electronics\", F:F)` — column C = product_category, column F = order_value.",
  },
  {
    prompt:
      "Your manager needs customer cities added to the orders sheet: \"Can you pull in each customer's city from the Customers tab, matched by customer_id?\"\n\nWrite an XLOOKUP (or VLOOKUP) formula that looks up customer_id in the Customers sheet and returns the city column.",
    hint: "`=XLOOKUP(B2, Customers!A:A, Customers!C:C)` — B2 = customer_id, Customers!A = customer_id lookup range, Customers!C = city return range.",
  },
];

/** Generate 2 real-world analyst scenarios appropriate for this lesson. */
export function buildSimilarProblems(lesson = {}, taskType) {
  const resolved = taskType || resolveLessonTaskType(lesson);
  const combined = [lesson.title, lesson.concept, lesson.lesson_prompt].filter(Boolean).join(' ');
  const cl = detectClauses(combined);
  const language = resolved === 'sql' ? 'duckdb' : resolved === 'excel' ? 'text' : 'python';

  if (resolved === 'excel') {
    return EXCEL_SCENARIOS.map((s) => ({ ...s, language: 'excel' }));
  }

  if (resolved !== 'sql') {
    return PYTHON_SCENARIOS.map((s) => ({ ...s, language: 'python' }));
  }

  // SQL: pick the two most relevant scenarios by detected clause pattern
  const pick = (arr) => arr.map((s) => ({ ...s, language }));

  if (cl.hasGroupBy && cl.hasHaving) return pick(SQL_SCENARIOS_BY_PATTERN.groupHaving);
  if (cl.hasJoin)                    return pick(SQL_SCENARIOS_BY_PATTERN.join);
  if (cl.hasCount || (cl.hasGroupBy && !cl.hasHaving)) return pick(SQL_SCENARIOS_BY_PATTERN.countGroup);
  if (cl.hasWhere || cl.hasOrderBy)  return pick(SQL_SCENARIOS_BY_PATTERN.whereOrder);
  return pick(SQL_SCENARIOS_BY_PATTERN.generic);
}

// ── Practice journal formatting ───────────────────────────────────────────

const SOLVE_LABELS = {
  split:   'S — Split the problem',
  observe: 'O — Observe the data',
  look:    'L — Look up tools & approach',
  verify:  'V — Verify your logic',
  evolve:  'E — Evolve the solution',
};

const FIVE_HOW_LABELS = {
  practical: 'Write it — code the solution',
  logic:     'Explain it — pseudocode',
  analogy:   'Compare it — analogy',
  story:     'Use it — job story',
  visual:    'See it — expected output',
};

function extractResponseText(key, resp, taskType) {
  if (!resp || typeof resp === 'undefined') return '';
  const lines = [];

  // StructuredStep
  if (resp.answers && typeof resp.answers === 'object') {
    Object.values(resp.answers).forEach((a) => { if (a?.trim()) lines.push(`- ${a.trim()}`); });
  }
  // CodeRunnerStep / CodeEditorStep — code then output
  if (resp.code?.trim()) {
    const lang = resp.language || taskType || 'text';
    lines.push(`\`\`\`${lang}\n${resp.code.trim()}\n\`\`\``);
    if (resp.output?.trim()) {
      lines.push(`**Output:**\n\`\`\`\n${resp.output.trim()}\n\`\`\``);
    }
    if (resp.error?.trim()) {
      lines.push(`**Error:** \`${resp.error.trim()}\``);
    }
  }
  // LogicFormStep
  if (resp.text?.trim()) lines.push(resp.text.trim());
  // AnalogyStep
  if (resp.analogy?.trim()) lines.push(`**My analogy:** ${resp.analogy.trim()}`);
  if (resp.because?.trim()) lines.push(`**Why it works:** ${resp.because.trim()}`);
  if (resp.limit?.trim())   lines.push(`**Where it breaks down:** ${resp.limit.trim()}`);
  // StoryStep
  if (resp.role?.trim())    lines.push(`**My role:** ${resp.role.trim()}`);
  if (resp.problem?.trim()) lines.push(`**The problem:** ${resp.problem.trim()}`);
  if (resp.story?.trim())   lines.push(resp.story.trim());
  // ToolSelectorStep
  if (resp.tool?.trim())    lines.push(`Tool: **${resp.tool}**`);
  // Plain string fallback
  if (typeof resp === 'string' && resp.trim()) lines.push(resp.trim());

  return lines.join('\n\n');
}

/** Format a completed practice attempt as a markdown journal section. */
export function formatPracticeJournalEntry(item, responses = {}, framework = 'solve') {
  const date = new Date().toISOString().slice(0, 10);
  const taskType = item?.context?.task_type || 'generic';
  const label = framework === 'solve' ? 'SOLVE' : '5-HOW';
  const keys = framework === 'solve'
    ? Object.keys(SOLVE_LABELS)
    : Object.keys(FIVE_HOW_LABELS);
  const labelMap = framework === 'solve' ? SOLVE_LABELS : FIVE_HOW_LABELS;

  const isCohort = Boolean(item?.context?.is_cohort);
  const roleTitle = item?.title || 'Practice session';
  const problemTitle = item?.context?.problem_title;
  const headingTitle = isCohort && problemTitle ? `${roleTitle} — ${problemTitle}` : roleTitle;

  // <!-- framework:solve --> / <!-- framework:five-how --> marker lets upsertPracticeEntry
  // find and replace this specific block without touching the other framework's entry.
  let md = `\n---\n<!-- framework:${framework} -->\n\n## ${headingTitle}\n`;
  md += `**Completed:** ${date} | **Method:** ${label}\n\n`;

  // Business scenario — prefer the concept intro stored on context, fall back to item prompt
  const scenario = (item?.context?.concept_scenario || item?.prompt || '').trim();
  if (scenario) {
    const cleaned = scenario.replace(/\*\*/g, '').replace(/\n/g, '\n> ');
    md += `> **Scenario:** ${cleaned}\n\n`;
  }

  if (isCohort) {
    const workspaceUrl = item.context.workspace_url;
    const datasetUrl = item.context.dataset_url;
    if (workspaceUrl) md += `**Shared workbook:** [Open workbook](${workspaceUrl})\n\n`;
    if (datasetUrl) {
      const fullUrl = datasetUrl.startsWith('http')
        ? datasetUrl
        : `https://clarity.insitechinternational.com${datasetUrl}`;
      md += `**Raw dataset:** [Download CSV](${fullUrl})\n\n`;
    }
  }

  for (const key of keys) {
    const resp = responses[key];
    if (!resp) continue;
    const text = extractResponseText(key, resp, taskType);
    if (!text.trim()) continue;
    md += `### ${labelMap[key]}\n${text}\n\n`;
  }

  // Include "Try it" scenario responses from 5-HOW
  if (framework === 'five-how') {
    [1, 2].forEach((i) => {
      const resp = responses[`similar_${i}`];
      if (!resp) return;
      const text = extractResponseText(`similar_${i}`, resp, taskType);
      if (!text.trim()) return;
      const scenarioPrompt = item?.similarProblems?.[i - 1]?.prompt?.trim();
      md += `### Try it — Scenario ${i}\n`;
      if (scenarioPrompt) md += `> ${scenarioPrompt.replace(/\n/g, '\n> ')}\n\n`;
      md += `${text}\n\n`;
    });
  }

  return md;
}

// Removes duplicate framework entries from stored practice_markdown (e.g. from
// before markers were introduced) and ensures SOLVE always precedes 5-HOW.
export function dedupePracticeMarkdown(markdown) {
  if (!markdown?.trim()) return markdown || '';
  const SEP = '\n---\n';
  const parts = markdown.split(SEP);
  const seen = new Set();
  const kept = [];

  // Scan from end → keep the LAST (most recent) occurrence of each framework
  for (let i = parts.length - 1; i >= 0; i--) {
    const part = parts[i];
    const fw = part.includes('**Method:** SOLVE') ? 'solve'
      : part.includes('**Method:** 5-HOW') ? 'five-how'
      : null;
    if (fw) {
      if (!seen.has(fw)) { seen.add(fw); kept.unshift(part); }
      // else duplicate — discard
    } else {
      kept.unshift(part);
    }
  }

  // Guarantee SOLVE comes before 5-HOW
  const si = kept.findIndex((p) => p.includes('**Method:** SOLVE'));
  const fi = kept.findIndex((p) => p.includes('**Method:** 5-HOW'));
  if (si !== -1 && fi !== -1 && fi < si) {
    [kept[si], kept[fi]] = [kept[fi], kept[si]];
  }

  return kept.join(SEP);
}

// Keeps SOLVE and 5-HOW entries side-by-side, always in that order.
// Removes ALL existing sections for this framework (old unmarked + new marked),
// then inserts the fresh entry: SOLVE before any 5-HOW block, 5-HOW at the end.
export function upsertPracticeEntry(existingMarkdown, newEntry, framework) {
  const marker = `<!-- framework:${framework} -->`;
  const methodLabel = framework === 'solve' ? 'SOLVE' : '5-HOW';
  const SEP = '\n---\n';

  const parts = existingMarkdown.split(SEP);
  const filtered = parts.filter(
    (part) => !part.includes(marker) && !part.includes(`**Method:** ${methodLabel}`),
  );

  if (framework === 'five-how') {
    // 5-HOW always goes after SOLVE — append
    return filtered.join(SEP) + newEntry;
  }

  // SOLVE goes before any existing 5-HOW section
  const fiveHowIdx = filtered.findIndex(
    (p) => p.includes('<!-- framework:five-how -->') || p.includes('**Method:** 5-HOW'),
  );
  if (fiveHowIdx === -1) {
    return filtered.join(SEP) + newEntry;
  }
  const before = filtered.slice(0, fiveHowIdx).join(SEP);
  const after = filtered.slice(fiveHowIdx).join(SEP);
  return before + newEntry + SEP + after;
}

// ── SQL practice sandbox seed data (unchanged) ──────────────────────────

export const SQL_SAMPLE_SETUP = `
CREATE OR REPLACE TABLE orders (
  order_id INTEGER, customer_id INTEGER, customer_name TEXT,
  product_category TEXT, product_name TEXT,
  order_value DECIMAL(10,2), order_date DATE, status TEXT, region TEXT
);
INSERT INTO orders VALUES
  (1,  101, 'Alice Chen',    'Electronics',  'Laptop',        1200.00, '2024-01-05', 'completed', 'North'),
  (2,  102, 'Bob Smith',     'Clothing',     'Jacket',          89.50, '2024-01-06', 'completed', 'South'),
  (3,  101, 'Alice Chen',    'Electronics',  'Mouse',           45.00, '2024-01-08', 'completed', 'North'),
  (4,  103, 'Carol White',   'Home & Garden','Lamp',            62.00, '2024-01-09', 'completed', 'East'),
  (5,  104, 'Dan Brown',     'Clothing',     'Trainers',       120.00, '2024-01-10', 'completed', 'West'),
  (6,  102, 'Bob Smith',     'Electronics',  'Headphones',     250.00, '2024-01-12', 'completed', 'South'),
  (7,  105, 'Eve Davis',     'Home & Garden','Sofa',           850.00, '2024-01-14', 'completed', 'North'),
  (8,  103, 'Carol White',   'Clothing',     'Dress',           55.00, '2024-01-15', 'pending',   'East'),
  (9,  106, 'Frank Miller',  'Electronics',  'Tablet',         499.00, '2024-01-16', 'completed', 'South'),
  (10, 104, 'Dan Brown',     'Home & Garden','Chair',          210.00, '2024-01-18', 'completed', 'West'),
  (11, 101, 'Alice Chen',    'Clothing',     'Scarf',           28.00, '2024-01-20', 'completed', 'North'),
  (12, 107, 'Grace Lee',     'Electronics',  'Phone',          799.00, '2024-01-21', 'completed', 'East'),
  (13, 105, 'Eve Davis',     'Clothing',     'Coat',           195.00, '2024-01-22', 'pending',   'North'),
  (14, 106, 'Frank Miller',  'Home & Garden','Desk',           340.00, '2024-01-24', 'completed', 'South'),
  (15, 107, 'Grace Lee',     'Electronics',  'Keyboard',        75.00, '2024-01-25', 'completed', 'East');
CREATE OR REPLACE TABLE customers (
  customer_id INTEGER, customer_name TEXT, city TEXT, join_date DATE
);
INSERT INTO customers VALUES
  (101, 'Alice Chen',   'London',     '2023-06-01'),
  (102, 'Bob Smith',    'Manchester', '2023-07-15'),
  (103, 'Carol White',  'Birmingham', '2023-08-20'),
  (104, 'Dan Brown',    'Leeds',      '2023-09-10'),
  (105, 'Eve Davis',    'London',     '2023-10-05'),
  (106, 'Frank Miller', 'Bristol',    '2023-11-12'),
  (107, 'Grace Lee',    'Edinburgh',  '2023-12-01');
`;
